import * as THREE from 'three';
import { PlanogramObject,Anchor } from "./PlanogramObject.js";
import { PlanogramRenderer } from "./PlanogramRenderer.js";
import { PlanogramContainer } from "./PlanogramContainer.js";
import { PlanogramRendererResources, PlanogramSubcomponentAttributes, PlanogramObjectAttributes } from './Resources.js';
import { PlanogramImage } from './PlanogramImage.js';
import { PlanogramComponent } from './PlanogramComponent.js';
import { PlanogramDivider } from './PlanogramDivider.js';
import { PlanogramPeg } from './PlanogramPeg.js';

/**
 * @typedef {Object} PropertyData
 * @property {"numeric"|"boolean"|"string"} type
 * @property {number|boolean|string} defaultValue
 * @property {string} prefix
 */

export class PlanogramSubcomponent extends HTMLElement {

    //#region Attributes
    static CommonAttributes = PlanogramSubcomponentAttributes.CommonAttributes;
    static BackBoardAttributes = PlanogramSubcomponentAttributes.BackBoardAttributes;
    static PegBoardClipStripSlotwallAttributes = PlanogramSubcomponentAttributes.PegBoardClipStripSlotwallAttributes;
    static ShelfAttributes = PlanogramSubcomponentAttributes.ShelfAttributes
    static ChestAttributes = PlanogramSubcomponentAttributes.ChestAttributes;
    //#endregion

    static SubComponentAttributes = {
        MerchRow1 : "merch-row-1",
        MerchRow2 : "merch-row-2",
        Notches : "notches",
        Fill: "fill",
        Riser: "riser"
    }

    ShaderMappings = new Map([
        [PlanogramSubcomponent.SubComponentAttributes.MerchRow1, new CompactAttribute(new Map([ 
            [PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.Width, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.Height, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.StartX, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.StartY, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.SpacingX, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.SpacingY, new CompactProperty("numeric", 0)]
        ]))],
        [PlanogramSubcomponent.SubComponentAttributes.MerchRow2, new CompactAttribute(new Map([
            [PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.Width, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.Height, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.StartX, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.StartY, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.SpacingX, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes.SpacingY, new CompactProperty("numeric", 0)]
        ]))],
        [PlanogramSubcomponent.SubComponentAttributes.Notches, new CompactAttribute(new Map([
            [PlanogramSubcomponent.BackBoardAttributes.ShowNotches, new CompactProperty("boolean", false)],
            [PlanogramSubcomponent.BackBoardAttributes.NotchType, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.BackBoardAttributes.NotchWidth, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.BackBoardAttributes.NotchHeight, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.BackBoardAttributes.NotchStartX, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.BackBoardAttributes.NotchStartY, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.BackBoardAttributes.NotchSpacingX, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.BackBoardAttributes.NotchSpacingY, new CompactProperty("numeric", 0)]
        ]))],
        [PlanogramSubcomponent.SubComponentAttributes.Fill, new CompactAttribute(new Map([
            [PlanogramSubcomponent.CommonAttributes.FillFace, new CompactProperty("enum", null)],
            [PlanogramSubcomponent.CommonAttributes.FillColour, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.CommonAttributes.FillStyle, new CompactProperty("numeric", 0)]
        ]), (maps) => this.#seperateFillMap(maps))],
        [PlanogramSubcomponent.SubComponentAttributes.Riser, new CompactAttribute(new Map([
            [PlanogramSubcomponent.ShelfAttributes.RiserColour, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.ShelfAttributes.RiserFillStyle, new CompactProperty("numeric", 0)],
            [PlanogramSubcomponent.ShelfAttributes.RiserTransparency, new CompactProperty("numeric", 0)],
        ]))],
     ]);

    #attributeValueMap = new Map();
    get attributeValueMap(){return this.#attributeValueMap;}

    #fillAlphasMap = new Map();

    static get observedAttributes(){
        return [
            ...Object.values(PlanogramObjectAttributes.Attributes),
            ...Object.values(PlanogramSubcomponent.SubComponentAttributes),
            ...Object.values(PlanogramSubcomponent.CommonAttributes),
            ...Object.values(PlanogramSubcomponent.BackBoardAttributes),
            ...Object.values(PlanogramSubcomponent.PegBoardClipStripSlotwallAttributes),
            ...Object.values(PlanogramSubcomponent.ShelfAttributes),
            ...Object.values(PlanogramSubcomponent.ChestAttributes) 
        ];
    }

    //#region Public Properties

    get group (){ return this.#PlanogramComponent?.group; }
    get totalMeshCount(){
        var count = 1;
        if(!this.#hideChestWalls && this.#hasChestWalls)
        {
            count = 0;
            if(this.faceThicknessBottom > 0) count++;
            if(this.faceThicknessTop > 0) count++;
            if(this.faceThicknessLeft > 0) count++;
            if(this.faceThicknessRight > 0) count++;
            if(this.faceThicknessFront > 0) count++;
            if(this.faceThicknessBack > 0) count++;
            if(this.#needsFrontFacePlane) count++;

        }
        if(this.#hasRisers) 
            count += [this.showFront, this.showBack, this.showLeft, this.showRight].reduce((a,c)=> a + c >> 0 , 0);
        count += this.#dividers.length;
        if(this.hasPegHoles && this.#pegs.length > 0){
            for(var peg of this.#pegs){
                count ++;
                if(peg.drawTag)count++;
                if(peg.drawUpper)count++;
            }
        }
        return count;
    }

    get planogramSubComponentId(){
        return [...PlanogramContainer.get(this).querySelectorAll("planogram-subcomponent")].indexOf(this);
    }

    get nonRotatedBoundingBox(){
        if(this.sizes.length === this.positions.length){
            return this.sizes.reduce((a,c,i)=>{ 
                const center = this.positions[i];
                const size = new THREE.Vector3(c.x, c.y, c.z);
                const box = new THREE.Box3().setFromCenterAndSize(center, size);
                return a.union(box);
            }, new THREE.Box3());
        }
        return new THREE.Box3();
    }

    get groupBoundingBox(){
        if(this.sizes.length === this.positions.length){
            const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(
                this.rotationX, this.rotationY, this.rotationZ, 'XYZ'));
            
            return this.sizes.reduce((a,c,i)=>{
                const center = this.positions[i];
                const halfSize = new THREE.Vector3(c.x / 2, c.y / 2, c.z / 2);
                const corners = [
                    new THREE.Vector3(-halfSize.x, -halfSize.y, -halfSize.z),
                    new THREE.Vector3(-halfSize.x, -halfSize.y,  halfSize.z),
                    new THREE.Vector3(-halfSize.x,  halfSize.y, -halfSize.z),
                    new THREE.Vector3(-halfSize.x,  halfSize.y,  halfSize.z),
                    new THREE.Vector3( halfSize.x, -halfSize.y, -halfSize.z),
                    new THREE.Vector3( halfSize.x, -halfSize.y,  halfSize.z),
                    new THREE.Vector3( halfSize.x,  halfSize.y, -halfSize.z),
                    new THREE.Vector3( halfSize.x,  halfSize.y,  halfSize.z)
                ];
                const transformedCorners = corners.map(corner => {
                    return corner.applyMatrix4(rotationMatrix).add(center);
                });
                const rotatedBox = new THREE.Box3();
                transformedCorners.forEach(corner => rotatedBox.expandByPoint(corner));
                
                return a.union(rotatedBox);
            }, new THREE.Box3());
        }
        return new THREE.Box3();
    }

    firstRender = true;

    /**
     * The nominal width/height/depth block centred on x/y/z - the "real" box - with no
     * rotation applied. Used where the caller applies its own rotation transform separately
     * (e.g. the selection-box decorator, which drives a per-instance rotation matrix).
     * @returns {THREE.Box3}
     */
    get realBoundingBox() {
        return new THREE.Box3().setFromCenterAndSize(
            new THREE.Vector3(this.x, this.y, this.z),
            new THREE.Vector3(this.width, this.height, this.depth));
    }

    /**
     * Bounding box of only the main block (or chest wall faces when set), excluding risers,
     * pegs, and dividers. Used for resize/rotation gizmo calculations so those extras don't
     * inflate the transform pivot or committed size.
     * @returns {THREE.Box3}
     */
    get mainBlockBoundingBox() {
        const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(
            new THREE.Euler(this.rotationX, this.rotationY, this.rotationZ, 'XYZ'));
        const center = new THREE.Vector3(this.x, this.y, this.z);
        const hx = this.width / 2, hy = this.height / 2, hz = this.depth / 2;
        const box = new THREE.Box3();
        for (const sx of [-hx, hx])
            for (const sy of [-hy, hy])
                for (const sz of [-hz, hz])
                    box.expandByPoint(new THREE.Vector3(sx, sy, sz).applyMatrix4(rotationMatrix).add(center));
        return box;
    }

    //#region Geometry Attribute Properties
    
    /** @returns {THREE.Vector3[]} */
    get sizes(){
        const sizes = [ new THREE.Vector3(this.width, this.height, this.depth) ];
        if(!this.#hideChestWalls && this.#hasChestWalls){
            var sideOffset = (this.depth - this.faceThicknessFront - this.faceThicknessBack) / this.depth;
            sizes.pop();
            if(this.faceThicknessBottom > 0)sizes.push(new THREE.Vector3(this.width, this.faceThicknessBottom, this.depth));
            if(this.faceThicknessTop > 0)sizes.push(new THREE.Vector3(this.width, this.faceThicknessTop, this.depth));
            if(this.faceThicknessLeft > 0)sizes.push(new THREE.Vector3(this.faceThicknessLeft, this.height - this.faceThicknessBottom - this.faceThicknessTop, this.depth * sideOffset));
            if(this.faceThicknessRight > 0)sizes.push(new THREE.Vector3(this.faceThicknessRight, this.height - this.faceThicknessBottom - this.faceThicknessTop, this.depth * sideOffset));
            if(this.faceThicknessFront > 0)sizes.push(new THREE.Vector3(this.width, this.height - this.faceThicknessBottom - this.faceThicknessTop, this.faceThicknessFront));
            if(this.faceThicknessBack > 0)sizes.push(new THREE.Vector3(this.width, this.height - this.faceThicknessBottom - this.faceThicknessTop, this.faceThicknessBack));
            if(this.#needsFrontFacePlane)sizes.push(new THREE.Vector3(this.width, this.height, PlanogramSubcomponent.#FrontFacePlaneThickness));
        }
        if(this.#hasRisers){
            if(this.showFront) sizes.push(new THREE.Vector3(this.width, this.riserHeight, this.riserThickness));
            if(this.showBack) sizes.push(new THREE.Vector3(this.width, this.riserHeight, this.riserThickness));
            if(this.showLeft) sizes.push(new THREE.Vector3(this.riserThickness, this.riserHeight, this.depth));
            if(this.showRight) sizes.push(new THREE.Vector3(this.riserThickness, this.riserHeight, this.depth));
        }
        if(this.hasDividers)
            sizes.push(...this.#dividers.map(divider => new THREE.Vector3(divider.width, divider.height, divider.depth)));
        if(this.#pegs.length > 0){
            for(var peg of this.#pegs){
                sizes.push(new THREE.Vector3(this.merchConstraintRow1Width, this.merchConstraintRow1Height, peg.depth));
                if(peg.drawTag) sizes.push(new THREE.Vector3(peg.tagWidth, peg.tagHeight, 0.3));
                if(peg.drawUpper) sizes.push(new THREE.Vector3(this.merchConstraintRow1Width, this.merchConstraintRow1Height, peg.depth));
            }
        }
        return sizes;
    }

        /** @returns {THREE.Vector3[]} */
    get positions(){
        const positions = [];
        const defaultMatrix = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(
            this.rotationX, this.rotationY, this.rotationZ, 'XYZ')).multiply(new THREE.Matrix4().makeTranslation(this.x, this.y, this.z));

        if(this.#hasChestWalls && !this.#hideChestWalls ){
            
            const bottom = this.faceThicknessBottom /2 - this.height/2;
            if(this.faceThicknessBottom > 0)
                positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().multiply(new THREE.Matrix4().makeTranslation( 0, bottom, 0 ))));
            if(this.faceThicknessTop > 0)
                positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().multiply(new THREE.Matrix4().
                    makeTranslation( 0, this.height/2 - this.faceThicknessTop/2, 0 ))));
            if(this.faceThicknessLeft > 0)
                positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().multiply(new THREE.Matrix4().
                    makeTranslation( -this.width/2 + this.faceThicknessLeft/2, bottom + this.height/2 - this.faceThicknessTop/2, this.faceThicknessBack/2 - this.faceThicknessFront/2  ))));
            if(this.faceThicknessRight > 0)
                positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().multiply(new THREE.Matrix4().
                    makeTranslation( this.width/2 - this.faceThicknessRight/2, bottom + this.height/2 - this.faceThicknessTop/2, this.faceThicknessBack/2 - this.faceThicknessFront/2  ))));
            if(this.faceThicknessFront > 0)
                positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().multiply(new THREE.Matrix4().
                    makeTranslation( 0, bottom + this.height/2 - this.faceThicknessTop/2, this.depth/2 - this.faceThicknessFront/2 ))));
            if(this.faceThicknessBack > 0)
                positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().multiply(new THREE.Matrix4().
                    makeTranslation( 0, bottom + this.height/2 - this.faceThicknessTop/2, -this.depth/2 + this.faceThicknessBack/2 ))));
            if(this.#needsFrontFacePlane)
                positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().multiply(new THREE.Matrix4().
                    makeTranslation( 0, 0, this.depth/2 + PlanogramSubcomponent.#FrontFacePlaneThickness/2 ))));
        }else {
            positions.push( new THREE.Vector3().setFromMatrixPosition(defaultMatrix.clone().
                multiply(new THREE.Matrix4().makeTranslation(0, this.#hasChestWalls ? this.faceThicknessBottom /2 - this.height/2 : 0, 0))));
        }

        if( this.totalMeshCount > 1 ){
            const topSurface = this.height/2 + (this.#hasChestWalls && !this.#hideChestWalls ? this.faceThicknessTop/2 : 0);
            if(this.showFront)
                positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( 0,topSurface + this.riserHeight/2,this.depth/2 + this.riserThickness/2 ))));
            if(this.showBack)
                positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( 0,topSurface + this.riserHeight/2, -this.depth/2 - this.riserThickness/2 ))));
            if(this.showLeft)
                positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( -this.width/2 - this.riserThickness/2,topSurface + this.riserHeight/2,0 ))));
            if(this.showRight)
                positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( this.width/2 + this.riserThickness/2,topSurface + this.riserHeight/2,0 ))));
        }

        if(this.hasDividers){
            for(var divider of this.#dividers){
                positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( 
                        divider.x - this.width/2 + divider.width/2, 
                        divider.y - this.height/2 + divider.height/2, 
                       divider.z - this.depth/2 + divider.depth/2 ))));
            }
        }

        if(this.#pegs.length > 0){
            for(var peg of this.#pegs){
                const pegX = peg.x - this.width/2;
                const pegY = peg.y - this.height/2;
                positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( pegX, pegY, this.depth/2 + peg.depth/2 ))));
                if(peg.drawTag) positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                    multiply(new THREE.Matrix4().makeTranslation( pegX, pegY + peg.tagYOffset - peg.tagHeight/2, this.depth/2 + peg.depth ))));
                if(peg.drawUpper){
                    const pegTagTop = pegY + peg.tagYOffset - peg.tagHeight/2;
                    positions.push( new THREE.Vector3().setFromMatrixPosition( defaultMatrix.clone().
                        multiply(new THREE.Matrix4().makeTranslation(pegX, pegTagTop + (peg.tagHeight/2 - this.merchConstraintRow1Height/2), this.depth/2 + peg.depth/2 ))));
                }
            }
        }

        return positions;
    }


    /** @returns {number[]} */
    get partTypes(){
        const options = [ this.#PlanogramComponent.type ];
        if(!this.#hideChestWalls && this.#hasChestWalls){
            var chestTransparency = PlanogramRenderer.get(this).chestWallTransparency;
            var transparency = chestTransparency > 0 ? this.transparency * chestTransparency : 0;
            var walls = [];
            if(this.faceThicknessBottom > 0) walls.push(PlanogramComponent.Types.Chest_Wall_Bottom);
            if(this.faceThicknessTop > 0) walls.push(PlanogramComponent.Types.Chest_Wall_Top);
            if(this.faceThicknessLeft > 0) walls.push(PlanogramComponent.Types.Chest_Wall_Left);
            if(this.faceThicknessRight > 0) walls.push(PlanogramComponent.Types.Chest_Wall_Right);
            if(this.faceThicknessFront > 0) walls.push(PlanogramComponent.Types.Chest_Wall_Front);
            if(this.faceThicknessBack > 0) walls.push(PlanogramComponent.Types.Chest_Wall_Back);
            if(this.#needsFrontFacePlane) walls.push(PlanogramComponent.Types.Chest_Notch_Plane);
            options.splice(0,options.length,...walls).flat();
        }

        if(this.#hasRisers){
            if(this.showFront) options.push( PlanogramComponent.Types.Shelf_Riser );
            if(this.showBack) options.push( PlanogramComponent.Types.Shelf_Riser );
            if(this.showLeft) options.push( PlanogramComponent.Types.Shelf_Riser );
            if(this.showRight) options.push( PlanogramComponent.Types.Shelf_Riser );
        }
        if(this.hasDividers)
            options.push(...this.#dividers.map(divider => PlanogramComponent.Types.Divider));
        if(this.#pegs.length > 0){
            for(var peg of this.#pegs){
                options.push( PlanogramComponent.Types.PegProng );
                if(peg.drawTag) options.push( PlanogramComponent.Types.PegTag );
                if(peg.drawUpper) options.push( PlanogramComponent.Types.PegProng );
            }
        }
        return options;
    }

    /** @returns {Map<string, number>} */
    get imageDepths(){
        const images = this.#images;
        const imageMap = new Map();
        for(const [side, index] of [...Object.entries(PlanogramImage.sides)]){
            const image = images.find(img => img.placement?.toLocaleLowerCase() === side.toLocaleLowerCase());
            imageMap.set( side, image ? image.depth : -1);
        }
        return imageMap;
    }

    /** @returns {number[]} */
    get registryValues(){
        const values = [...this.#attributeValueMap.entries()]
            .reduce((a,[,c])=>[...a,...[...c.values()]], []);
        values.push(this.lineColour);
        values.push(this.lineThickness);
        values.push(this.transparency);
        values.push(this.selected);

        values.push(...[...this.imageDepths.values()]);
        values.push(...["front","back","top","bottom","left","right"].map(f => this.#fillAlphasMap.get(f) ?? -1.0));
        values.push(this.faceThicknessFront > 0 ? this.height : 0);
        return values.filter(x=>typeof(x) !== 'string').map(x=>typeof(x) === 'boolean' ? x ? 1 : 0 : x );
    }

    //#endregion

    //#region Common Attributes
    get width() { return Number(this.getAttribute(PlanogramSubcomponent.CommonAttributes.Width)) || 10; }
    get height() { return Number(this.getAttribute(PlanogramSubcomponent.CommonAttributes.Height)) || 10; }
    get depth() { return Number(this.getAttribute(PlanogramSubcomponent.CommonAttributes.Depth)) || 10; }

    get x() { return Number(this.getAttribute(PlanogramObject.Attributes.X)) || 0;}
    get y() { return Number(this.getAttribute(PlanogramObject.Attributes.Y)) || 0;}
    get z() { return Number(this.getAttribute(PlanogramObject.Attributes.Z)) || 0;}

    get rotationX() { return Number(this.getAttribute(PlanogramObject.Attributes.RotationX)) || 0;}
    get rotationY() { return Number(this.getAttribute(PlanogramObject.Attributes.RotationY)) || 0;}
    get rotationZ() { return Number(this.getAttribute(PlanogramObject.Attributes.RotationZ)) || 0;}

    get lineThickness() { return Number(this.getAttribute(PlanogramObject.Attributes.LineThickness)) || 0;}
    get lineColour() { return PlanogramObject.computeColour(this.getAttribute(PlanogramObject.Attributes.LineColour), 0x000000);}
    get transparency() { return Number(this.getAttribute(PlanogramObject.Attributes.Transparency)) || 1;}
    get selected() { return this.hasAttribute(PlanogramObject.Attributes.Selected); }
    get snapAnchor(){  return this.getAttribute(PlanogramSubcomponent.CommonAttributes.SnapAnchor); }
    //#endregion

    //#region Shelf Attributes
    get riserHeight() { return Number(this.getAttribute(PlanogramSubcomponent.ShelfAttributes.RiserHeight)) || 5; }
    get riserThickness() { return Number(this.getAttribute(PlanogramSubcomponent.ShelfAttributes.RiserThickness)) || 1; }
    get riserTransparency() {
        if(!this.hasAttribute(PlanogramSubcomponent.ShelfAttributes.RiserTransparency))return 1;
        var value = Number(this.getAttribute(PlanogramSubcomponent.ShelfAttributes.RiserTransparency)); 
        return isNaN(value) ? 1 : value; 
    }
    get showBack() { return this.hasAttribute(PlanogramSubcomponent.ShelfAttributes.ShowBack); }
    get showFront() { return this.hasAttribute(PlanogramSubcomponent.ShelfAttributes.ShowFront); }
    get showLeft() { return this.hasAttribute(PlanogramSubcomponent.ShelfAttributes.ShowLeft); }
    get showRight() { return this.hasAttribute(PlanogramSubcomponent.ShelfAttributes.ShowRight); }
    //#endregion

    //#region Chest Attributes
    get faceThicknessFront() { return Number(this.getAttribute(PlanogramSubcomponent.ChestAttributes.FaceThicknessFront)) || 0; }
    get faceThicknessBack() { return Number(this.getAttribute(PlanogramSubcomponent.ChestAttributes.FaceThicknessBack)) || 0; }
    get faceThicknessLeft() { return Number(this.getAttribute(PlanogramSubcomponent.ChestAttributes.FaceThicknessLeft)) || 0; }
    get faceThicknessRight() { return Number(this.getAttribute(PlanogramSubcomponent.ChestAttributes.FaceThicknessRight)) || 0; }
    get faceThicknessTop() { return Number(this.getAttribute(PlanogramSubcomponent.ChestAttributes.FaceThicknessTop)) || 0; }
    get faceThicknessBottom() { return Number(this.getAttribute(PlanogramSubcomponent.ChestAttributes.FaceThicknessBottom)) || 0; }
    //#endregion

    //#endregion
    
    //#region Private Properties
    /**@type {PlanogramComponent} */
    #PlanogramComponent = null;
    /**@type {PlanogramImage[]} */
    get #images(){ return [...this.querySelectorAll("planogram-image")].filter(x=>x.isConnected); }
    get #hasRisers (){ return (this.showFront || this.showBack || this.showLeft|| this.showRight); }
    get #hasChestWalls(){ return this.faceThicknessFront > 0 || this.faceThicknessBack > 0 || this.faceThicknessLeft > 0 || this.faceThicknessRight > 0 || this.faceThicknessTop > 0 || this.faceThicknessBottom > 0; }
    get #hideChestWalls(){ return PlanogramContainer.get(this)?.hideChestWalls; }
    /** Chest walls replace the main block, so notches/peg holes need a thin stand-in plane flush with the front face. */
    get #needsFrontFacePlane(){ return !this.#hideChestWalls && this.#hasChestWalls && (this.faceThicknessFront > 0 || this.faceThicknessTop > 0 || this.faceThicknessBottom > 0); }
    static #FrontFacePlaneThickness = 0.1;
    get canHaveDividers(){ return this.#PlanogramComponent.type === PlanogramComponent.Types.Shelf || this.#PlanogramComponent.type === PlanogramComponent.Types.Chest || this.#PlanogramComponent.type === PlanogramComponent.Types.Custom; }
    get hasDividers(){return this.canHaveDividers && this.#dividers.length > 0;}
    /**@type {PlanogramDivider[]} */
    get #dividers(){ return [...this.querySelectorAll("planogram-divider")].filter(x=> Reflect.has(x, "firstRender") && x.firstRender === false); }
    /**@type {PlanogramPeg[]} */
    get #pegs(){ return [...this.querySelectorAll("planogram-peg")].filter(x=>Reflect.has(x, "firstRender") && x.firstRender === false); }
     /**@type {Anchor[]} */
    #anchors = [];
    get snapAnchors(){ return this.#anchors; }

    //#region Object Properties

    //#region Peg Hole Properties

    #merchRow1Map = new Map();
    #merchRow2Map = new Map();
    #notchesMap = new Map();
    get #merchRow1(){ return this.getAttribute(PlanogramSubcomponent.SubComponentAttributes.MerchRow1); } 
    get #merchRow2(){ return this.getAttribute(PlanogramSubcomponent.SubComponentAttributes.MerchRow2); }
    get #notches(){ return this.getAttribute(PlanogramSubcomponent.SubComponentAttributes.Notches); }

    
    //#endregion

    //#endregion

    //#endregion


    constructor() { super(); }

    //#region Public Methods
    /**
     * Updating this subcomponent's instances
     * @param {THREE.InstancedMesh} mesh 
     * @param {number} currentCount 
     */
    generateSubcomponent( mesh, currentCount ){
        if(!mesh || !mesh.isInstancedMesh || mesh.count <= 0)return;
        
        var meshCount = currentCount;
        if(this.sizes.length === this.positions.length){
            for(var i = 0; i < this.sizes.length; i++){
                const size = this.sizes[i];
                const position = this.positions[i];
                const matrix = new THREE.Matrix4().makeTranslation(position.x, position.y, position.z).multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(
                    this.rotationX, this.rotationY, this.rotationZ, 'XYZ')))
                    .multiply(new THREE.Matrix4().makeScale(size.x, size.y, size.z));
                mesh.setMatrixAt( meshCount++, matrix);
            }
        }

    }

    dividerPlug(){
        this.#PlanogramComponent?.subComponentPlug(true);
        const container = PlanogramContainer.get(this);
    }

    pegsPlug(){
        this.#PlanogramComponent?.subComponentPlug(true);
        const container = PlanogramContainer.get(this);
    }

    imagePlug(){
        this.#PlanogramComponent?.subComponentPlug();
        const container = PlanogramContainer.get(this);
    }

    //#endregion

    //#region Private Methods
    
    /**
     * 
     * @param {string} style 
     * @param {number} defaultStyle 
     * @returns {number}
     */
    #computeStyle(style, defaultStyle = 0){
        if(this.hasAttribute(style))return Number(this.getAttribute(style));
        return defaultStyle;
    }

    #seperateFillMap(maps){
        const newMap = new Map();
        const front = maps.find(x=>x.get(PlanogramSubcomponent.CommonAttributes.FillFace) === "F");

        ["F","BK","T","B","L","R"].forEach(face => {
            const map = maps.find(x=>x.get(PlanogramSubcomponent.CommonAttributes.FillFace) === face) ?? front;
            const prefix = face === "F" ? "front" : face === "BK" ? "back" : face === "T" ? "top" : face === "B" ? "bottom" : face === "L" ? "left" : "right";
            if(map){
                const colourInt = (Number(map.get(PlanogramSubcomponent.CommonAttributes.FillColour)) || 0) >>> 0;
                const hasAlpha = colourInt > 0xFFFFFF;
                newMap.set(prefix+"Fill", hasAlpha ? colourInt & 0xFFFFFF : colourInt);
                newMap.set(prefix+"Style", map.get(PlanogramSubcomponent.CommonAttributes.FillStyle));
                this.#fillAlphasMap.set(prefix, hasAlpha ? ((colourInt >>> 24) & 0xFF) / 255.0 : -1.0);
            }else{
                newMap.set(prefix+"Fill", 0);
                newMap.set(prefix+"Style", 0);
                this.#fillAlphasMap.set(prefix, -1.0);
            }
        });
        return newMap;
    }

    /**
     * 
     * @param  {...string} attributesNames
     * @returns {Map<string, Map<string, string | number | boolean>>} a map of attribute names and their values 
     */
    #extractAttributes( ...attributesNames ){
        const entries = attributesNames.length > 0 ? [...this.ShaderMappings.entries()].filter(([attribute, mapping]) => attributesNames.includes(attribute)) : [...this.ShaderMappings.entries()];
        entries.forEach(([attribute, mapping]) => {
            const value = this.getAttribute(attribute);
            this.#attributeValueMap.set(attribute, mapping.parse(value));
        });

        return this.#attributeValueMap;
    }


    //#endregion

    //#region Event Raise Methods
    /**
     * 
     * @param {THREE.Vector3} oldPosition 
     * @param {THREE.Vector3} newPosition 
     * @param {DataTransfer} dataTransfer 
     */
    raiseDropEvent( oldPosition, newPosition, dataTransfer ){
        const renderer = PlanogramRenderer.get(this);
        const delta =  new THREE.Vector3().fromArray(newPosition.toArray().map(x=>Math.round(x)));
        const event = new CustomEvent( PlanogramObject.customEvents.ObjectDropped, { bubbles: true, cancelable: true, detail: { x:newPosition.x ,y:newPosition.y, z:newPosition.z } });
        
        if( this.dispatched( event ) && renderer.showMessages ){
            console.groupCollapsed("Element Dropped");
            console.log("Old Position:", oldPosition );
            console.log("New Position:", newPosition );
            console.log("Delta:", delta );
            console.log("Event: ", event);
            console.log("Target: ", this);
            console.groupEnd();
        }
        

    }

    raiseClickEvent( x, y, rightClick = false){
        const renderer = PlanogramRenderer.get(this);
        const rect = renderer.domElement.getBoundingClientRect();
        const clickEvent = new MouseEvent( "click", { bubbles: true, cancelable: true, view: window, clientX: rect.x + x, clientY: rect.y + y });
        Object.defineProperty(clickEvent, 'button', { ...Object.getOwnPropertyDescriptor(clickEvent, 'button'), get: function() {return rightClick ? 2 : 0;}});
        Object.defineProperty(clickEvent, 'target', { ...Object.getOwnPropertyDescriptor(clickEvent, 'target'), get: (function() {return this;}).bind(this)});
        if( this.dispatchEvent(clickEvent) && renderer.showMessages ){
            console.groupCollapsed("Object Selected"+ (rightClick? " and Context Menu":""));
            console.log("Target: ", this);
            console.log("Event: ", clickEvent);
            console.groupEnd();
        }
    }
    
    dispatched( event ){
        const renderer = PlanogramRenderer.get(this)?.showMessages ?? false;
        const dropped = this.dispatchEvent(event);
        return dropped && renderer;
    }
    //#endregion

    //#region Web Component Lifecycle

    attributeChangedCallback(name, oldValue, newValue) {
        
        if (this.firstRender || oldValue === newValue ) return;

        const remeshableShelfAttributes =[
            PlanogramSubcomponent.ShelfAttributes.RiserHeight,
            PlanogramSubcomponent.ShelfAttributes.RiserThickness,
            PlanogramSubcomponent.ShelfAttributes.ShowBack,
            PlanogramSubcomponent.ShelfAttributes.ShowFront,
            PlanogramSubcomponent.ShelfAttributes.ShowLeft,
            PlanogramSubcomponent.ShelfAttributes.ShowRight,
        ];
        const remeshable = [
            PlanogramSubcomponent.CommonAttributes.Width, 
            PlanogramSubcomponent.CommonAttributes.Height, 
            PlanogramSubcomponent.CommonAttributes.Depth,
            ...Object.values(PlanogramSubcomponent.ChestAttributes),
            ...remeshableShelfAttributes,
            PlanogramObject.Attributes.X, 
            PlanogramObject.Attributes.Y, 
            PlanogramObject.Attributes.Z,
            PlanogramObject.Attributes.RotationX, 
            PlanogramObject.Attributes.RotationY, 
            PlanogramObject.Attributes.RotationZ
        ];
        if([
            PlanogramSubcomponent.SubComponentAttributes.MerchRow1,
            PlanogramSubcomponent.SubComponentAttributes.MerchRow2,
            PlanogramSubcomponent.SubComponentAttributes.Notches,
            PlanogramSubcomponent.SubComponentAttributes.Fill,
            PlanogramSubcomponent.SubComponentAttributes.Riser
        ].includes(name)) this.#extractAttributes(name);
        if(name === PlanogramSubcomponent.CommonAttributes.SnapAnchor){
            this.#anchors = Anchor.fromString(newValue);
        }
        this.#PlanogramComponent?.subComponentPlug(remeshable.includes(name));
        const container = PlanogramContainer.get(this);
    }

    connectedCallback() {
        this.#extractAttributes();
        if(this.parentElement instanceof PlanogramComponent){
            this.firstRender = false;
            this.#PlanogramComponent = this.parentElement;
            const container = PlanogramContainer.get(this);
            this.#PlanogramComponent.subComponentPlug(true);
            this.#anchors = Anchor.fromString(this.snapAnchor);
        }
    }

    disconnectedCallback(){
        this.dispose();
    }

    dispose() {
        const container = PlanogramContainer.get(this);
        container?.subcomponentDataController?.unregister(this);
        this.#PlanogramComponent?.subComponentPlug(true);
    }
    //#endregion

    
}

class CompactProperty{
    /**@type {"numeric"|"boolean"|"enum"} */
    type;
    /**@type {number|boolean|string} */
    defaultValue;
    /**
     * 
     * @param {"numeric"|"boolean"|"enum"} type 
     * @param {number|boolean|string} defaultValue 
     */
    constructor( type, defaultValue ){
        this.type = type;
        this.defaultValue = defaultValue;
    }
}

/**
 * Callback short description
 *
 * Long description
 *
 * @callback postProcessCallback
 * @param {Map<string,string|number|boolean>[]} maps The first number in the computation
 * @returns {Map<string,string|number|boolean>}
 */

class CompactAttribute{
    /**@type {Map<string, CompactProperty>} */
    propertyMap = new Map();
    /**@type {postProcessCallback} */
    postProcessCallback = null;
    /**
     * 
     * @param {Map<string, CompactProperty>} propertyMap 
     * @param {postProcessCallback} postProcessCallback 
     */
    constructor( propertyMap, postProcessCallback = null ){
        this.propertyMap = propertyMap
        this.postProcessCallback = postProcessCallback;
    }
    /**
     * @param {string} value
     * @returns {Map<string, any>} Map of property keys to their parsed values, or default values if parsing fails
     */
    parse( value ){
        const propertyMap = this.propertyMap;
        const placeholder = "__PROPERTY__";
        const defaultNumericGroup = `(?<${placeholder}>[\\d-]+(?:[.,]\\d+)?)`;
        const defaultStringGroup = `(?<${placeholder}>[\\A-z-]+)`;
        const defaultBooleanGroup = `(?<${placeholder}>true|false)`;
        const defaultMap = new Map([...propertyMap.entries()].map(([key, value]) => [key, value.defaultValue]));
        const emptyResult = () => this.postProcessCallback ? this.postProcessCallback([]) : defaultMap;

        if(!value) return emptyResult();

        const regexString = [...propertyMap.entries()].reduce((acc, [key, value]) => {
            var group = value.type === "enum" ? defaultStringGroup.replaceAll(placeholder, key) :
            value.type === "boolean" ? defaultBooleanGroup.replaceAll(placeholder, key) :
                defaultNumericGroup.replaceAll(placeholder, key);
            if( Reflect.has(value, "prefix") && value.prefix) group = `(${value.prefix}${group})` ;
            acc.push(group);
            return acc;
        }, []).join("\\s");

        if(!new RegExp(regexString, "g").test(value)) return emptyResult();

        const maps = [];
        const regexp = new RegExp(regexString, "g");
        for(var match = regexp.exec(value); match;match = regexp.exec(value)){
            const resultMap = new Map([...propertyMap.entries()].map(([key, value]) => {
                let parsedValue;
                if (value.type === "enum") {
                    parsedValue = match.groups[key];
                } else if (value.type === "numeric") {
                    parsedValue = Number(match.groups[key]);
                } else if (value.type === "boolean") {
                    parsedValue = match.groups[key] === "true";
                }
                return [key, parsedValue === undefined ? value.defaultValue : parsedValue];
            }));
            maps.push(resultMap);
        }

        const finalisedResults = this.postProcessCallback ?
            this.postProcessCallback( maps ) : maps.at(0);
        if(!finalisedResults || Array.isArray(finalisedResults))return defaultMap;

        return finalisedResults;
    }
}